import { ExternalDataService, LanguageService, useTranslation } from '@repo/roche-xx-shared';
import { RocheUIDropdown, RocheUIGrid } from '@rocheglobal/roche-ui';
import React, { useEffect, useRef, useState } from 'react';
import { RocheLoadingSpinner } from './roche-loader-spinner';

interface ListProps {
  country: string;
}

interface ListResponse {
  canListen: boolean;
  Items: unknown[];
  Page: number;
  Total: number;
  headers: string[];
}

const getRow = (item: Record<string, unknown>) => [
  item['Country of the patient Organisation'],
  item.Division,
  item['Website of Patient Organisation'] && item['Website of Patient Organisation'] !== 'N/A'
    ? `
      <roche-generic-link
        target="_blank"
        url="${item['Website of Patient Organisation']}"
        content="${item['Receiving Patient Organisation']}"
      />`
    : item['Receiving Patient Organisation'],
  item['Type of Support'],
  item.Currency,
  item.Amount,
  // TODO: Replace with React RocheUITooltip component once roche-responsive-table
  // is migrated from Stencil to React and supports JSX elements instead of HTML strings
  `<div style="position: relative">
    <roche-tooltip icon="info" style="position:relative">${item['Project description']}</roche-tooltip>
  </div>`,
];

const Table = (props: ListProps): JSX.Element => {
  const { country } = props;
  const [state, setState] = useState({
    isFetching: false,
    items: [],
    total: 0,
    page: 1,
    canListen: false,
    headers: [],
  });
  const container = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const { current }: { current: HTMLElement } = container;
    if (!current) {
      return;
    }

    current.parentElement.addEventListener('rochePaginationPageChange', (event: CustomEvent) =>
      setState({ ...state, page: event.detail }),
    );
  }, [state.canListen]);

  const countryOfPatient = useTranslation('country of the patient organisation');
  const division = useTranslation('division');
  const patientOrganisation = useTranslation('patient organisation');
  const typeOfSupport = useTranslation('type of support');
  const currency = useTranslation('currency');
  const amount = useTranslation('amount');

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isFetching: true }));
    ExternalDataService.getPatients({ country, page: state.page })
      .then(async (response: ListResponse) => {
        const { Items, Page, Total } = response;

        setState({
          ...state,
          canListen: true,
          items: Items.map(getRow),
          page: Page,
          total: Total,
          headers: [countryOfPatient, division, patientOrganisation, typeOfSupport, currency, amount],
        });
      })
      .finally(() => setState((prevState) => ({ ...prevState, isFetching: false })));
  }, [state.page]);

  if (state.isFetching) {
    return <RocheLoadingSpinner />;
  }

  if (!state.items.length) {
    return <roche-empty-state></roche-empty-state>;
  }

  return (
    <div ref={container}>
      <roche-responsive-table rows={JSON.stringify(state.items)} headers={JSON.stringify(state.headers)} />

      {state.total / itemsPerPage > 1 && (
        <roche-pagination
          total-pages={Math.ceil(state.total / itemsPerPage)}
          active-page={state.page}
        ></roche-pagination>
      )}
    </div>
  );
};

// biome-ignore lint: no-explicit-any: better typings are welcome
export const RochePatientOrganisations = ({ slot }: any): JSX.Element => {
  const [countries, setCountries] = useState([]);

  const [currentCountry, setCurrentCountry] = useState('');
  const container = useRef(null);
  const selectCountryLabel = useTranslation('select country of the patient organisation');
  const pleaseSelectLabel = useTranslation('please select:');
  const isRightToLeft = LanguageService.isRightToLeft();

  const handleCountryChange = (value: string) => {
    setCurrentCountry(value);
  };

  useEffect(() => {
    ExternalDataService.getPatientCountries().then(async (response: string[]) => {
      setCountries(response);
    });
  }, []);

  if (countries.length <= 0) {
    return null;
  }

  const rocheDropdownItems = countries.map((country) => ({ label: country, value: country }));

  return (
    <div slot={slot} ref={container}>
      <RocheUIGrid layout='8-4' fullWidth={true} isRightToLeft={isRightToLeft}>
        <RocheUIDropdown
          slot={isRightToLeft ? 'right' : 'left'}
          label={selectCountryLabel}
          required
          options={rocheDropdownItems}
          placeholder={pleaseSelectLabel}
          value={currentCountry}
          onChange={handleCountryChange}
        />
      </RocheUIGrid>
      {countries.map((country, index) => (
        <div key={index}>{currentCountry === country && <Table country={country} />}</div>
      ))}
    </div>
  );
};
